import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./ProjectRequirement.css";
import ProjectTabs from "./ProjectTabs";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/project-requirements/combined/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;
const REFERENCE_VALUES_URL = `${API_BASE}/reference-values/`;
const SCOPE_CATALOGS_URL = `${API_BASE}/scope-catalogs/`;
const PROJECTS_URL = `${API_BASE}/projects/`;

const createRequirement = () => ({
  id: null,
  role_code: "",
  required_count: 1,
  minimum_experience_years: "0",
  is_mandatory: true,
  remarks: "",
  scopes: []
});

const toNumberString = (value, fallback = "0") =>
  value !== null && value !== undefined ? String(value) : fallback;

const extractScopes = (row) => {
  const list = row?.requirement_scopes || row?.scopes || [];
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (item && typeof item === "object") {
        return String(item.scope ?? item.id ?? "");
      }
      return String(item);
    })
    .filter(Boolean);
};

const ProjectRequirementForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [tenantId, setTenantId] = useState(null);
  const [projectId, setProjectId] = useState(null);

  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);

  const [roleOptions, setRoleOptions] = useState([]);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState([createRequirement()]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("tenant_user");
      const parsed = raw ? JSON.parse(raw) : null;

      if (!parsed?.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant information missing",
          text: "Please login again."
        });
        return;
      }

      setTenantId(Number(parsed.tenant_id));
    } catch (err) {
      console.error("Invalid tenant_user:", err);
      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again."
      });
    }

    const pid =
      params.projectId ||
      params.project_id ||
      params.id ||
      location.state?.projectId ||
      location.state?.project_id ||
      null;

    if (pid) setProjectId(Number(pid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.projectId, params.project_id, params.id, location.state]);

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const [refRes, scopeRes, projRes] = await Promise.all([
        axios.get(REFERENCE_VALUES_URL).catch(() => ({ data: [] })),
        axios.get(SCOPE_CATALOGS_URL).catch(() => ({ data: [] })),
        axios.get(PROJECTS_URL).catch(() => ({ data: [] }))
      ]);

      const refData = refRes.data?.data || refRes.data || [];
      const scopeData = scopeRes.data?.data || scopeRes.data || [];
      const projData = projRes.data?.data || projRes.data || [];

      // ---- Roles: value = reference_value.id, label = "OPTION_TYPE-CODE"
      const roles = (Array.isArray(refData) ? refData : [])
        .filter((item) => {
          if (item?.is_active === false) return false;

          const type =
            item?.option_set_value?.option_type ||
            item?.option_type ||
            item?.category ||
            item?.type ||
            "";

          if (!type) return true;
          return String(type).toUpperCase() === "PROFESSIONAL_ROLE";
        })
        .map((item) => {
          const type = String(
            item?.option_set_value?.option_type ||
              item?.option_type ||
              item?.category ||
              item?.type ||
              ""
          ).toUpperCase();

          const code = String(
            item?.code ?? item?.value ?? ""
          );

          return {
            value: String(item.id),
            label: type && code ? `${type}-${code}` : code || type,
            code,
            type
          };
        })
        .filter((item) => item.value);

      // ---- Scopes ----
      const scopes = (Array.isArray(scopeData) ? scopeData : [])
        .filter((item) => item?.is_active !== false)
        .map((item) => ({
          value: String(item.id),
          label:
            item.scope_name ||
            item.name ||
            item.code ||
            `Scope #${item.id}`
        }));

      // ---- Projects ----
      const projects = (Array.isArray(projData) ? projData : [])
        .filter((item) => item?.is_active !== false)
        .map((item) => ({
          value: String(item.id),
          label:
            item.project_name ||
            item.project_title ||
            item.name ||
            item.title ||
            item.code ||
            `Project #${item.id}`
        }));

      setRoleOptions(roles);
      setScopeOptions(scopes);
      setProjectOptions(projects);
    } catch (error) {
      console.error("Error fetching options:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load reference data",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Could not load roles, scopes, and projects."
      });
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (tenantId) fetchRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, projectId]);

const fetchRequirements = async () => {
  try {
    setLoading(true);

    const response = await axios.post(
      TENANT_RECORDS_API_URL,
      {
        tenant_id: tenantId,
        tables: ["project_requirements"]
      },
      { headers: { "Content-Type": "application/json" } }
    );

    const result = response.data;
    console.log("Project requirements response:", result);

    let rows = [];
    if (result?.data?.project_requirements) {
      rows = result.data.project_requirements;
    } else if (result?.project_requirements) {
      rows = result.project_requirements;
    } else if (Array.isArray(result?.data)) {
      rows = result.data;
    } else if (Array.isArray(result)) {
      rows = result;
    }

    // Only filter by tenant when the field is actually present
    rows = rows.filter((r) => {
      if (r?.tenant === undefined || r?.tenant === null) return true;
      return Number(r.tenant) === Number(tenantId);
    });

    // Only filter by project when the field is actually present
    if (projectId) {
      rows = rows.filter((r) => {
        if (r?.project === undefined || r?.project === null) return true;
        return Number(r.project) === Number(projectId);
      });
    }

    setRequirements(rows);
  } catch (error) {
    console.error("Error fetching requirements:", error);
    Swal.fire({
      icon: "error",
      title: "Unable to load requirements",
      text:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Something went wrong while loading requirements."
    });
  } finally {
    setLoading(false);
  }
};

  // ---- Lookup helpers (values are ids as strings) ----
  const getRoleLabel = useCallback(
    (value) => {
      if (value === null || value === undefined || value === "") return "-";
      const match = roleOptions.find(
        (r) => String(r.value) === String(value)
      );
      return match ? match.label : value;
    },
    [roleOptions]
  );

  const getScopeLabel = useCallback(
    (value) => {
      if (value === null || value === undefined || value === "") return "-";
      const match = scopeOptions.find(
        (s) => String(s.value) === String(value)
      );
      return match ? match.label : String(value);
    },
    [scopeOptions]
  );

  const getProjectLabel = useCallback(
    (value) => {
      if (value === null || value === undefined || value === "") return "-";
      const match = projectOptions.find(
        (p) => String(p.value) === String(value)
      );
      return match ? match.label : `Project #${value}`;
    },
    [projectOptions]
  );

  const handleRequirementChange = (requirementIndex, e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) =>
      prev.map((requirement, index) =>
        index === requirementIndex
          ? {
              ...requirement,
              [name]: type === "checkbox" ? checked : value
            }
          : requirement
      )
    );
  };

  const handleAdd = () => {
    setFormData([createRequirement()]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, createRequirement()]);
  };

  const handleRemoveForm = (requirementIndex) => {
    if (formData.length === 1) return;
    setFormData((prev) =>
      prev.filter((_, i) => i !== requirementIndex)
    );
  };

  const handleEdit = (row) => {
    const scopes = extractScopes(row);

    setFormData([
      {
        id: row.id,
        // stored backend value is a pk → keep as string for the <select>
        role_code:
          row.role_code !== null && row.role_code !== undefined
            ? String(row.role_code)
            : "",
        required_count: toNumberString(row.required_count, "1"),
        minimum_experience_years: toNumberString(
          row.minimum_experience_years,
          "0"
        ),
        is_mandatory:
          row.mandatory !== undefined
            ? Boolean(row.mandatory)
            : row.is_mandatory !== undefined
            ? Boolean(row.is_mandatory)
            : true,
        remarks: row.remarks || "",
        scopes
      }
    ]);

    if (row.project) setProjectId(Number(row.project));

    setEditingId(row.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Requirement?",
      text: `Are you sure you want to delete the "${getRoleLabel(
        row.role_code
      )}" requirement?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);

      await axios.delete(`${API_URL}${row.id}/`);

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Requirement deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      await fetchRequirements();
    } catch (error) {
      console.error("Error deleting requirement:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Unable to delete the requirement."
      });
    } finally {
      setLoading(false);
    }
  };

  const addScope = (requirementIndex, scopeValue) => {
    if (!scopeValue) return;

    setFormData((prev) =>
      prev.map((requirement, index) => {
        if (index !== requirementIndex) return requirement;
        if (requirement.scopes.includes(scopeValue)) return requirement;
        return {
          ...requirement,
          scopes: [...requirement.scopes, scopeValue]
        };
      })
    );
  };

  const removeScope = (requirementIndex, scopeIndex) => {
    setFormData((prev) =>
      prev.map((requirement, index) =>
        index === requirementIndex
          ? {
              ...requirement,
              scopes: requirement.scopes.filter(
                (_, i) => i !== scopeIndex
              )
            }
          : requirement
      )
    );
  };

  // ---- Payload ---- (role_code as pk integer)
  const buildPayload = (req) => ({
    tenant: Number(tenantId),
    project: Number(projectId),
    role_code: Number(req.role_code),
    required_count: Number(req.required_count),
    minimum_experience_years: Number(
      req.minimum_experience_years || 0
    ),
    mandatory: Boolean(req.is_mandatory),
    remarks: req.remarks || "",
    requirement_scopes: (req.scopes || []).map((scope) => ({
      scope_catalog: Number(scope)
    }))
  });

  const validateRow = (req, index) => {
    const label = `Requirement ${index + 1}`;

    const isEmptyRow =
      !req.role_code &&
      !req.required_count &&
      !req.minimum_experience_years &&
      !req.remarks &&
      (!req.scopes || req.scopes.length === 0) &&
      !req.id;

    if (isEmptyRow) return true;

    if (!req.role_code) {
      Swal.fire({
        icon: "warning",
        title: "Role Required",
        text: `Please select a role for ${label}.`
      });
      return false;
    }

    if (Number(req.required_count) < 1) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Required Count",
        text: `Required Count must be at least 1 for ${label}.`
      });
      return false;
    }

    if (
      req.minimum_experience_years !== "" &&
      Number(req.minimum_experience_years) < 0
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Experience",
        text: `Minimum Experience must be non-negative for ${label}.`
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });
      return;
    }

    if (!projectId) {
      Swal.fire({
        icon: "error",
        title: "Project ID missing",
        text: "Please select a project before saving."
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (req) =>
        req.role_code ||
        req.remarks ||
        (req.scopes && req.scopes.length > 0) ||
        req.id
    );

    const CONFIG = { headers: { "Content-Type": "application/json" } };

    try {
      setLoading(true);

      if (isEditing) {
        await axios.put(
          `${API_URL}${editingId}/`,
          buildPayload(formData[0]),
          CONFIG
        );

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const req of activeRows) {
          await axios.post(API_URL, buildPayload(req), CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} requirement${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([createRequirement()]);

      await fetchRequirements();
    } catch (error) {
      console.error("Error saving requirements:", error);

      const backendError =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data ||
        "Unable to save requirements.";

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text:
          typeof backendError === "string"
            ? backendError
            : JSON.stringify(backendError)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([createRequirement()]);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="requirement-page-container">
          <ProjectTabs />

          {!showForm ? (
            <>
              <div className="requirement-page-header">
                <div>
                  <h1>Project Requirements</h1>
                </div>

                <button
                  type="button"
                  className="requirement-add-top-button"
                  onClick={handleAdd}
                >
                  + Add Requirement
                </button>
              </div>

              <div className="requirement-list-card">
                {loading ? (
                  <div className="requirement-loading">
                    Loading requirements...
                  </div>
                ) : requirements.length === 0 ? (
                  <div className="requirement-empty">
                    <div className="requirement-empty-icon">+</div>
                    <h3>No Requirements Found</h3>
                    <p>
                      There are no requirement records available for
                      the current project.
                    </p>
                    <button
                      type="button"
                      className="requirement-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Requirement
                    </button>
                  </div>
                ) : (
                  <div className="requirement-table-wrapper">
                    <table className="requirement-table">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Role</th>
                          <th>Required Count</th>
                          <th>Min. Experience</th>
                          <th>Type</th>
                          <th>Remarks</th>
                          <th>Scopes</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {requirements.map((req) => (
                          <tr key={req.id}>
                            <td className="requirement-muted">
                              {getProjectLabel(req.project)}
                            </td>

                            <td>
                              <span className="requirement-role-pill">
                                {getRoleLabel(req.role_code)}
                              </span>
                            </td>

                            <td className="requirement-muted">
                              {req.required_count ?? "-"}
                            </td>

                            <td className="requirement-muted">
                              {req.minimum_experience_years ?? 0} yr
                            </td>

                            <td>
                              {req.mandatory ? (
                                <span className="requirement-mandatory-pill">
                                  Mandatory
                                </span>
                              ) : (
                                <span className="requirement-optional-pill">
                                  Optional
                                </span>
                              )}
                            </td>

                            <td className="requirement-muted">
                              {req.remarks || "-"}
                            </td>

                            <td>
                              <div className="requirement-pill-list">
                                {extractScopes(req).length === 0 ? (
                                  <span className="requirement-muted">
                                    -
                                  </span>
                                ) : (
                                  extractScopes(req).map((s, i) => (
                                    <span
                                      key={`${s}-${i}`}
                                      className="requirement-pill"
                                    >
                                      {getScopeLabel(s)}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>

                            <td>
                              <div className="requirement-actions">
                                <button
                                  type="button"
                                  className="requirement-edit-btn"
                                  onClick={() => handleEdit(req)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="requirement-delete-btn"
                                  onClick={() => handleDelete(req)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="requirement-page-header">
                <div>
                  <h1>
                    {isEditing
                      ? "Edit Requirement"
                      : "Create Requirements"}
                  </h1>
                </div>

                <button
                  type="button"
                  className="requirement-back-button"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              <form
                className="requirement-form"
                onSubmit={handleSubmit}
              >
                {formData.map((requirement, requirementIndex) => (
                  <div
                    className="requirement-card"
                    key={requirementIndex}
                  >
                    <div className="requirement-card-header">
                      <div>
                        <h2>
                          {isEditing
                            ? "Requirement Details"
                            : `Requirement ${requirementIndex + 1}`}
                        </h2>
                      </div>

                      <div className="requirement-header-actions">
                        <span className="requirement-number-badge">
                          #{requirementIndex + 1}
                        </span>

                        {!isEditing &&
                          formData.length > 1 && (
                            <button
                              type="button"
                              className="requirement-remove-button"
                              onClick={() =>
                                handleRemoveForm(requirementIndex)
                              }
                            >
                              Remove
                            </button>
                          )}
                      </div>
                    </div>

                    {/* ---- SINGLE SECTION ---- */}
                    <div className="requirement-section">
                      <div className="requirement-grid">
                        <div className="requirement-field">
                          <label>
                            Project <span>*</span>
                          </label>
                          <select
                            value={projectId ? String(projectId) : ""}
                            onChange={(e) =>
                              setProjectId(
                                e.target.value
                                  ? Number(e.target.value)
                                  : null
                              )
                            }
                            disabled={loadingOptions}
                          >
                            <option value="">
                              {loadingOptions
                                ? "Loading projects..."
                                : "Select project"}
                            </option>
                            {projectOptions.map((item) => (
                              <option
                                key={item.value}
                                value={item.value}
                              >
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="requirement-field">
                          <label>
                            Professional Role <span>*</span>
                          </label>
                          <select
                            name="role_code"
                            value={requirement.role_code}
                            onChange={(e) =>
                              handleRequirementChange(
                                requirementIndex,
                                e
                              )
                            }
                            disabled={loadingOptions}
                          >
                            <option value="">
                              {loadingOptions
                                ? "Loading roles..."
                                : "Select role"}
                            </option>
                            {roleOptions.map((item) => (
                              <option
                                key={item.value}
                                value={item.value}
                              >
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="requirement-field">
                          <label>
                            Required Count <span>*</span>
                          </label>
                          <input
                            type="number"
                            name="required_count"
                            value={requirement.required_count}
                            onChange={(e) =>
                              handleRequirementChange(
                                requirementIndex,
                                e
                              )
                            }
                            min="1"
                          />
                        </div>

                        <div className="requirement-field">
                          <label>
                            Minimum Experience (Years)
                          </label>
                          <input
                            type="number"
                            name="minimum_experience_years"
                            value={
                              requirement.minimum_experience_years
                            }
                            onChange={(e) =>
                              handleRequirementChange(
                                requirementIndex,
                                e
                              )
                            }
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>

                      <div
                        className="requirement-grid"
                        style={{ marginTop: "20px" }}
                      >
                        <div className="requirement-field">
                          <label>Requirement Type</label>
                          <label className="requirement-switch-label">
                            <input
                              type="checkbox"
                              name="is_mandatory"
                              checked={requirement.is_mandatory}
                              onChange={(e) =>
                                handleRequirementChange(
                                  requirementIndex,
                                  e
                                )
                              }
                            />
                            <span className="requirement-switch">
                              <span className="requirement-switch-dot"></span>
                            </span>
                            <span>
                              {requirement.is_mandatory
                                ? "Mandatory Requirement"
                                : "Optional Requirement"}
                            </span>
                          </label>
                        </div>
                      </div>

                      <div
                        className="requirement-field"
                        style={{ marginTop: "20px" }}
                      >
                        <label>Remarks</label>
                        <textarea
                          name="remarks"
                          value={requirement.remarks}
                          onChange={(e) =>
                            handleRequirementChange(
                              requirementIndex,
                              e
                            )
                          }
                          rows="3"
                          placeholder="Enter requirement remarks..."
                        />
                      </div>

                      <div style={{ marginTop: "22px" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "#374151",
                            fontSize: "13px",
                            fontWeight: 600
                          }}
                        >
                          Requirement Scopes
                        </label>

                        <div className="requirement-scope-selector">
                          <div className="requirement-field">
                            <select
                              defaultValue=""
                              onChange={(e) => {
                                addScope(
                                  requirementIndex,
                                  e.target.value
                                );
                                e.target.value = "";
                              }}
                              disabled={loadingOptions}
                            >
                              <option value="">
                                {loadingOptions
                                  ? "Loading scopes..."
                                  : "Select scope to add"}
                              </option>
                              {scopeOptions.map((item) => (
                                <option
                                  key={item.value}
                                  value={item.value}
                                  disabled={requirement.scopes.includes(
                                    item.value
                                  )}
                                >
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {requirement.scopes.length > 0 && (
                          <div className="requirement-scope-list">
                            {requirement.scopes.map(
                              (scope, scopeIndex) => (
                                <div
                                  className="requirement-scope-chip"
                                  key={`${scope}-${scopeIndex}`}
                                >
                                  <span>
                                    {getScopeLabel(scope)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeScope(
                                        requirementIndex,
                                        scopeIndex
                                      )
                                    }
                                  >
                                    ×
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        )}

                        {requirement.scopes.length === 0 && (
                          <div className="requirement-empty-scope">
                            <strong>No scopes added</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <div className="requirement-add-wrapper">
                    <button
                      type="button"
                      className="requirement-add-button"
                      onClick={handleAddAnother}
                    >
                      + Add Another Requirement
                    </button>
                  </div>
                )}

                <div className="requirement-form-actions">
                  <button
                    type="button"
                    className="requirement-cancel-button"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="requirement-save-button"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Requirement"
                      : `Save ${
                          formData.length > 1
                            ? "All Requirements"
                            : "Requirement"
                        }`}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectRequirementForm;